import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormGroup} from "@angular/forms";
import {Observable, of} from "rxjs";
import {debounceTime, map} from "rxjs/operators";
import {MapDirectionsService} from "@angular/google-maps";
import {StateService} from "../../services/state.service";
import {CoolLocalStorage} from "@angular-cool/storage";
import {StorageMap} from "@ngx-pwa/local-storage";

@Component({
    selector: 'app-preview-route-map',
    templateUrl: './preview-route-map.component.html',
    styleUrls: ['./preview-route-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PreviewRouteMapComponent implements OnInit {
  data: any;
  googleMapsLoaded = false;
  currentActiveRouteKey: string;

  @Input() parts: any;
  @Input() formGroup: UntypedFormGroup;
  @Input() form: any = {config: {parts: []}};
  @Input() metrics: any;
  @Input() buttonstyle: string;

  directionsResults: Observable<google.maps.DirectionsResult | undefined>;
  options: any = {
    zoom: 14,
    disableDefaultUI: true,
  }
  directionsRenderOption: any;
  departureMarker: any;
  stopOverMarker: any;
  destinationMarker: any;
  calculating=false;
  markerPositions: google.maps.LatLngLiteral[] = [];

  // Route pin markers are inlined as SVG data URIs (rather than fetched from
  // environment.mainWebsite + /assets) so they render in every context — the
  // standalone app and widgets embedded on third-party origins — without
  // depending on the icon files being deployed to the main-website host.
  // The pin outline (black teardrop) is shared; only the inner circle path and
  // colour differ per marker. The source SVGs' nested group transforms cancel
  // out, so the paths sit directly in the 0 0 30 45 viewBox.
  private static readonly PIN_OUTLINE = 'M14.8730455,26.2144091 C8.95759091,26.2144091 4.16304545,21.4198636 4.16304545,15.5044091 C4.16304545,9.59031818 8.95759091,4.79577273 14.8730455,4.79577273 C20.7871364,4.79577273 25.5830455,9.59031818 25.5830455,15.5044091 C25.5830455,21.4198636 20.7871364,26.2144091 14.8730455,26.2144091 M29.9957727,15.9094091 C29.9957727,7.62668182 23.2812273,0.912136364 14.9985,0.912136364 C6.71577273,0.912136364 -0.000136363637,7.62668182 -0.000136363637,15.9094091 C-0.000136363637,18.7621364 0.809863636,21.4185 2.19395455,23.6903182 L2.18440909,23.6985 L15.0094091,44.9998636 L27.8330455,23.6985 L27.8098636,23.6766818 C29.1871364,21.4103182 29.9957727,18.7566818 29.9957727,15.9094091';
  private static readonly PIN_CIRCLE_DEPARTURE = 'M14.5006732,30 C7.04547948,30 1,23.9555655 1,16.5 C1,9.04308797 7.04547948,3 14.5006732,3 C21.955867,3 28,9.04308797 28,16.5 C28,23.9555655 21.955867,30 14.5006732,30 Z';
  private static readonly PIN_CIRCLE_DESTINATION = 'M28,16.5 C28,23.9555655 21.955867,30 14.5006732,30 C7.04547948,30 1,23.9555655 1,16.5 C1,9.04308797 7.04547948,3 14.5006732,3 C21.955867,3 28,9.04308797 28,16.5';

  private pinIcon(circlePath: string, color: string): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="45" viewBox="0 0 30 45"><path d="${circlePath}" fill="${color}"/><path d="${PreviewRouteMapComponent.PIN_OUTLINE}" fill="#000000"/></svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  constructor(
    private mapDirectionsService: MapDirectionsService,
    private _state: StateService,
    private _vault: StorageMap,
  ) {
  }

  ngOnInit(): void {
    this.data = this.formGroup.controls;
    this.detectGoogleMapsLoaded();
  }

  detectGoogleMapsLoaded() {
    const self = this;
    if (!this.googleMapsLoaded) {
      if (typeof (google) !== 'undefined') {
        this.googleMapsLoaded = true;
        if (this.data.departureModel && this.data.departureModel.value && this.data.departureModel.value.gps && this.data.departureModel.value.gps.lat && this.data.departureModel.value.gps.lng) {
          self.recalculateRoute();
        }
      } else {
        setTimeout(function () {
          self.detectGoogleMapsLoaded();
        }, 1000);
      }
    }
  }

  recalculateRoute() {
    if(!this.calculating) {
      this.calculating = true;
      if(this.googleMapsLoaded) {
        const self = this;
        if(this.data.departureModel && this.data.departureModel.value && this.data.departureModel.value.gps && this.data.departureModel.value.gps.lat && this.data.departureModel.value.gps.lng) {
          this.options.center = {
            lat: this.data.departureModel.value.gps.lat,
            lng: this.data.departureModel.value.gps.lng
          };
        }

        if (this.data.departureModel && this.data.departureModel.value && this.data.departureModel.value.gps && this.data.departureModel.value.gps.lat && this.data.departureModel.value.gps.lng && this.data.destinationModel && this.data.destinationModel.value && this.data.destinationModel.value.gps && this.data.destinationModel.value.gps.lat && this.data.destinationModel.value.gps.lng) {
          const request: google.maps.DirectionsRequest = {
            destination: {
              lat: this.data.destinationModel.value.gps.lat,
              lng: this.data.destinationModel.value.gps.lng
            },
            origin: {
              lat: this.data.departureModel.value.gps.lat,
              lng: this.data.departureModel.value.gps.lng
            },
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: (this.form.config.distanceFormat  === 'km'? google.maps.UnitSystem.METRIC : google.maps.UnitSystem.IMPERIAL),
          };
          const stopOvers = this.getStopOvers();
          if (stopOvers) {
            request.waypoints = [];
            stopOvers.forEach((stopOver) => {
              if(stopOver.value) {
                request.waypoints.push({
                  location: {
                    lat: stopOver.value.gps.lat,
                    lng: stopOver.value.gps.lng
                  }
                });
                }
            });
          }

          // const key = `${this.data.destinationModel.value.gps.lat},${this.data.destinationModel.value.gps.lng}-${this.data.departureModel.value.gps.lat},${this.data.departureModel.value.gps.lng}`;

          const serializeKey = {
            departure: this.data.departureModel.value.gps,
            destination: this.data.destinationModel.value.gps,
            stopOvers: this.data.stopOvers.value
          }

          const key = JSON.stringify(serializeKey);

          //Store current key in object to detect changes
          this.currentActiveRouteKey = key;

          self._vault.get('directions').subscribe((directions: any) => {
            if (!directions || !directions[key]) {
              if(!directions) {
                directions = {};
              }
              directions[key] = 'loading';
              this._vault.set('directions', directions).subscribe(() => { });
              this.directionsResults = this.mapDirectionsService.route(request)
                .pipe(
                  map(response => response.result)
                )
              this.directionsResults.subscribe((finalResult: google.maps.DirectionsResult) => {
                directions[key] = finalResult;
                this.metrics = {
                  distance: 0,
                  duration: 0,
                  distanceDesc: 0,
                  durationDesc: 0,
                };

                directions[key].routes[0].legs.forEach((leg) => {
                  if(this._state.companySettings.distanceFormat === 'mi') {
                    /**
                     * Convert distance from meters to miles
                     */
                    leg.distance.value = (leg.distance.value * 0.000621371192).toFixed(1);
                  } else {
                    leg.distance.value = (leg.distance.value/1000).toFixed(1);
                  }
                  leg.distance.value = Math.round(leg.distance.value * 100) / 100

                  //set Distance and duration to metrics
                  if (directions && directions[key]) {
                    this.metrics = {
                      distance: this.metrics.distance + leg.distance.value,
                      duration: this.metrics.duration + leg.duration.value,
                      distanceDesc: this.metrics.distance + leg.distance.value,
                      durationDesc: Math.ceil(this.metrics.duration + leg.duration.value/60),
                    };
                  }
                });

                this.metrics.distance = (this._state.companySettings.decimalSeparator === '.' ? this.metrics.distance : this.metrics.distance.toString().replace('.', ','));

                directions[key].metrics = this.metrics;
                directions[key] = JSON.parse(JSON.stringify(directions[key]));
                this._vault.set('directions', directions).subscribe((result) => {
                  this.calculating = false;
                })
              })
            } else {
              if (directions[key] === 'loading') {
                setTimeout(function(){
                  self._vault.get('directions').subscribe((directions: any) => {
                    if (directions[key] && directions[key] !== 'loading') {
                      self.metrics = directions[key].metrics;
                      //load directions from state as observable
                      self.directionsResults = of(directions[key]);
                    } else {
                      directions[key] = null;
                      self._vault.set('directions', directions).subscribe(() => {
                        self.calculating = false;
                        self.recalculateRoute();
                      });
                    }
                  });
                }, 2000)
              } else {
                this.metrics = directions[key].metrics;
                //load directions from state as observable
                this.directionsResults = of(directions[key]);
                this.calculating = false;
              }
            }
          });
        } else {
          this.directionsResults = null;
          this.calculating = false;
        }

        this.directionsRenderOption = {
          draggable: false,
          suppressInfoWindows: true,
          markerOptions: {
            draggable: false,
            clickable: false,
            visible: false,
          },
          polylineOptions: {
            strokeColor: '#6a77e0',
            strokeOpacity: 0,
            icons: [{
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillOpacity: 1,
                scale: 3
              },
              offset: '0',
              repeat: '1px'
            }],
          }
        };
        this.departureMarker = {
          draggable: false,
          clickable: false,
          icon: {
            url: this.pinIcon(PreviewRouteMapComponent.PIN_CIRCLE_DEPARTURE, '#5FB501'),
            scaledSize: new google.maps.Size(30, 30),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(15, 15)
          }
        };
        this.stopOverMarker = {
          draggable: false,
          clickable: false,
          icon: {
            url: this.pinIcon(PreviewRouteMapComponent.PIN_CIRCLE_DESTINATION, '#ACD8FF'),
            scaledSize: new google.maps.Size(30, 30),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(15, 15)
          }
        };
        this.destinationMarker = {
          draggable: false,
          clickable: false,
          icon: {
            url: this.pinIcon(PreviewRouteMapComponent.PIN_CIRCLE_DESTINATION, '#EC0C0C'),
            scaledSize: new google.maps.Size(30, 30),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(15, 15)
          }
        };
      }
    }
  }

  getStopOvers() {
    // @ts-ignore
    return this.formGroup.controls['stopOversModel'].controls;
  }
}
